import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Stack,
  Typography,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import colors from "../../../assets/theme/colors";
import LogFormShell from "../../UI/LogFormShell";
import { buildCareProfileStoryShareText } from "../../../utils/careProfileStory";
import ProfileShareTimelineOptions from "../../../features/profileSharing/ProfileShareTimelineOptions";
import { printProfileShareSnapshot } from "../../../features/profileSharing/profileSharePrint";

const storyTheme = colors.semantic.profileFlow.careStory;

const getTone = (toneKey = "who") => storyTheme.tones[toneKey] || storyTheme.tones.who;

const CareProfileStoryShareSheet = ({
  open,
  onClose,
  story,
  sections,
  buildSharePayload,
  timelineControls = null,
  title,
}) => {
  const shareableChapters = useMemo(
    () => (
      Array.isArray(sections)
        ? sections
        : (story?.chapters || [])
    ).filter((chapter) => chapter.hasContent),
    [sections, story]
  );
  const selectionDefaults = useMemo(
    () => shareableChapters
      .map((chapter) => `${chapter.id}:${chapter.defaultSelected !== false}`)
      .join("|"),
    [shareableChapters]
  );
  const [selected, setSelected] = useState({});
  const [copyStatus, setCopyStatus] = useState("");

  useEffect(() => {
    setSelected(Object.fromEntries(
      selectionDefaults
        .split("|")
        .filter(Boolean)
        .map((item) => {
          const [id, value] = item.split(":");
          return [id, value === "true"];
        })
    ));
    setCopyStatus("");
  }, [open, selectionDefaults]);

  const selectedCount = shareableChapters.filter((chapter) => selected[chapter.id] !== false).length;

  const handleToggle = (chapterId) => {
    const willSelect = selected[chapterId] === false;
    setSelected((current) => ({
      ...current,
      [chapterId]: willSelect,
    }));
    if (chapterId === "timeline" && willSelect) {
      timelineControls?.loadTimeline?.();
    }
    setCopyStatus("");
  };

  const getSharePayload = () => (
    buildSharePayload
      ? buildSharePayload(selected)
      : buildCareProfileStoryShareText(story, selected)
  );

  const handleCopy = async () => {
    const { text } = getSharePayload();
    if (!text.trim()) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus("Copied");
    } catch (error) {
      setCopyStatus("Copy failed");
    }
  };

  const handleShare = async () => {
    const { text } = getSharePayload();
    if (!text.trim()) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: title || story?.shareTitle || "Care summary",
          text,
        });
        setCopyStatus("Shared");
        return;
      }
      await navigator.clipboard.writeText(text);
      setCopyStatus("Share is not available here. Copied instead.");
    } catch (error) {
      if (error?.name !== "AbortError") setCopyStatus("Share failed");
    }
  };

  const handlePrint = () => {
    const { text } = getSharePayload();
    if (!text.trim()) return;
    setCopyStatus(
      printProfileShareSnapshot(title || story?.shareTitle || "Care summary", text)
        ? "Use the print dialog to save as PDF."
        : "Print window was blocked."
    );
  };

  return (
    <LogFormShell
      open={open}
      onClose={onClose}
      title={title || story?.shareTitle || "Share care summary"}
      subtitle="Choose what to include:"
      compactTitle
      forceDrawer
      mobileBreakpoint="md"
      bodySx={{ px: { xs: 2, sm: 3 }, pt: 1.35, pb: 1.8 }}
      surfaceSx={{
        maxWidth: 720,
        mx: "auto",
        height: "auto",
        maxHeight: "min(92dvh, 92vh)",
        borderTopLeftRadius: 28,
        borderTopRightRadius: 28,
      }}
      footer={(
        <Stack spacing={0.8}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={0.8}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<ContentCopyRoundedIcon />}
              onClick={handleCopy}
              disabled={!selectedCount}
              sx={{ minHeight: 48, borderRadius: "16px", textTransform: "none", fontWeight: 500 }}
            >
              {story?.shareActionLabel || "Copy"}
            </Button>
            <Button
              variant="contained"
              fullWidth
              startIcon={<IosShareRoundedIcon />}
              onClick={handleShare}
              disabled={!selectedCount}
              sx={{
                minHeight: 48,
                borderRadius: "16px",
                textTransform: "none",
                fontWeight: 500,
                bgcolor: storyTheme.primary,
                color: colors.semantic.profileFlow.cardBg,
                "&:hover": { bgcolor: storyTheme.primaryDark },
              }}
            >
              Share
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<PrintRoundedIcon />}
              onClick={handlePrint}
              disabled={!selectedCount}
              sx={{ minHeight: 48, borderRadius: "16px", textTransform: "none", fontWeight: 500 }}
            >
              Print / PDF
            </Button>
          </Stack>
          {copyStatus ? (
            <Typography sx={{ textAlign: "center", color: storyTheme.muted, fontSize: "0.9rem", fontWeight: 750 }}>
              {copyStatus}
            </Typography>
          ) : null}
        </Stack>
      )}
    >
      <Stack spacing={0.8}>
        {shareableChapters.map((chapter) => {
          const tone = getTone(chapter.tone);
          const isSelected = selected[chapter.id] !== false;
          return (
            <Fragment key={chapter.id}>
              <ButtonBase
                onClick={() => handleToggle(chapter.id)}
                sx={{
                  width: "100%",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  p: 1.2,
                  borderRadius: "16px",
                  bgcolor: storyTheme.primarySoft,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "12px",
                    mr: 1.4,
                    bgcolor: tone.markerBg,
                    color: tone.headerInk,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                  }}
                >
                  {isSelected ? <CheckRoundedIcon sx={{ fontSize: 20 }} /> : null}
                </Box>
                <Typography sx={{ color: storyTheme.text, fontSize: "1.08rem", fontWeight: 500 }}>
                  {chapter.title}
                </Typography>
              </ButtonBase>
              {chapter.id === "timeline" && isSelected ? (
                <ProfileShareTimelineOptions controls={timelineControls} />
              ) : null}
            </Fragment>
          );
        })}
      </Stack>
    </LogFormShell>
  );
};

export default CareProfileStoryShareSheet;
